import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { useStore } from "@Store/";

import Container from "@components/Container";
import RichText from "@components/RichText";

import {
  MissionBackground,
  MissionContent,
  MissionHeader,
  MissionItem,
  MissionItemContent,
  MissionItemLine,
  MissionList,
  MissionMain,
  MissionOverlay,
  MissionSubtitle,
  MissionTitle,
  MissionVideo,
} from "./index.style";

gsap.registerPlugin(ScrollTrigger);

const getAssetUrl = (asset) => {
  const url = asset?.file?.url;

  if (!url) return null;

  return url.startsWith("//") ? `https:${url}` : url;
};

const OurMissionList = ({
  subTitle,
  list,
  video,
  placeholder,
  id,
}) => {
  const [store] = useStore();

  const sectionRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const listRef = useRef(null);
  const itemsRef = useRef([]);
  const smoothScrollRef = useRef(store?.smoothScroll);

  const sectionId = id || "our-mission";

  const videoUrl = getAssetUrl(video);
  const placeholderUrl = getAssetUrl(placeholder);
  const videoType = video?.file?.contentType || "video/mp4";

  smoothScrollRef.current = store?.smoothScroll;

  const addItemRef = (element, index) => {
    if (!element) return;

    itemsRef.current[index] = element;
  };

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const section = sectionRef.current;
    const titleElement = titleRef.current;
    const subtitleElement = subtitleRef.current;
    const listElement = listRef.current;
    const items = itemsRef.current.filter(Boolean);

    if (!section || !titleElement) return undefined;

    const refresh = () => {
      if (smoothScrollRef.current) {
        smoothScrollRef.current.refresh();
      } else {
        ScrollTrigger.refresh();
      }
    };

    const resize = () => {
      gsap.matchMediaRefresh();
      refresh();
    };

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add("(min-width:1024px)", () => {
        const headerTimeline = gsap.timeline({
          defaults: {
            duration: 0.9,
            ease: "power3.out",
          },
          scrollTrigger: {
            trigger: section,
            start: "top 75%",
            once: true,
            invalidateOnRefresh: true,
          },
        });

        headerTimeline.fromTo(
          titleElement,
          {
            x: -80,
            autoAlpha: 0,
          },
          {
            x: 0,
            autoAlpha: 1,
          },
        );

        if (subtitleElement) {
          headerTimeline.fromTo(
            subtitleElement,
            {
              y: -50,
              autoAlpha: 0,
            },
            {
              y: 0,
              autoAlpha: 1,
            },
            "-=0.55",
          );
        }

        let itemsTween = null;

        if (listElement && items.length > 0) {
          itemsTween = gsap.fromTo(
            items,
            {
              y: -45,
              autoAlpha: 0,
            },
            {
              y: 0,
              autoAlpha: 1,
              duration: 0.9,
              stagger: 0.14,
              ease: "power3.out",
              scrollTrigger: {
                trigger: listElement,
                start: "top 85%",
                once: true,
                invalidateOnRefresh: true,
              },
            },
          );
        }

        requestAnimationFrame(refresh);

        return () => {
          headerTimeline.kill();

          if (itemsTween) {
            itemsTween.kill();
          }
        };
      });

      mm.add("(max-width:1023px)", () => {
        const elements = [
          titleElement,
          subtitleElement,
          ...items,
        ].filter(Boolean);

        gsap.set(elements, {
          clearProps: "all",
        });
      });

      requestAnimationFrame(refresh);
    }, section);

    window.addEventListener("load", refresh);
    window.addEventListener("resize", resize);

    return () => {
      ctx.revert();

      window.removeEventListener("load", refresh);
      window.removeEventListener("resize", resize);

      ScrollTrigger.clearScrollMemory();

      itemsRef.current = [];
    };
  }, [list, subTitle]);

  return (
    <MissionMain
      ref={sectionRef}
      id={sectionId}
      aria-labelledby={`${sectionId}-title`}
    >
      <MissionBackground aria-hidden="true">
        {videoUrl && (
          <MissionVideo
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={placeholderUrl || undefined}
            tabIndex="-1"
            aria-hidden="true"
          >
            <source src={videoUrl} type={videoType} />
          </MissionVideo>
        )}

        {!videoUrl && placeholderUrl && (
          <img
            src={placeholderUrl}
            alt=""
            loading="lazy"
            aria-hidden="true"
          />
        )}
      </MissionBackground>

      <Container>
        <MissionContent>
          <MissionHeader>
            <MissionTitle
              ref={titleRef}
              id={`${sectionId}-title`}
            >
              <span>Our</span>

              <strong>mission</strong>
            </MissionTitle>

            {subTitle && (
              <MissionSubtitle ref={subtitleRef}>
                {subTitle}
              </MissionSubtitle>
            )}
          </MissionHeader>

          {list.length > 0 && (
            <MissionList ref={listRef}>
              {list.map((item, index) => (
                <MissionItem
                  ref={(element) => addItemRef(element, index)}
                  key={item?.id || item?.contentful_id || index}
                >
                  <MissionItemLine aria-hidden="true" />

                  <MissionItemContent>
                    <RichText content={item?.text} />
                  </MissionItemContent>
                </MissionItem>
              ))}
            </MissionList>
          )}
        </MissionContent>
      </Container>
    </MissionMain>
  );
};

OurMissionList.propTypes = {
  id: PropTypes.string,
  subTitle: PropTypes.string,
  video: PropTypes.object,
  placeholder: PropTypes.object,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      contentful_id: PropTypes.string,
      text: PropTypes.object,
    }),
  ),
};

OurMissionList.defaultProps = {
  id: "our-mission",
  subTitle: "",
  list: [],
  video: null,
  placeholder: null,
};

export default OurMissionList;